import { z } from 'zod'
import { userRefSchema } from './primitives.js'

/** Entidades sobre las que se puede comentar o abrir un seguimiento. */
export const entityTypeSchema = z.enum(['client', 'metric', 'activation'])
export type EntityType = z.infer<typeof entityTypeSchema>

export const commentSchema = z.object({
  id: z.string(),
  entityType: entityTypeSchema,
  entityId: z.string(),
  author: userRefSchema,
  cuerpo: z.string(),
  /** Ids de usuarios mencionados con @. */
  mentions: z.array(z.string()),
  createdAt: z.string(),
})
export type Comment = z.infer<typeof commentSchema>

export const createCommentSchema = z.object({
  entityType: entityTypeSchema,
  entityId: z.string(),
  cuerpo: z.string().min(1).max(2000),
  mentions: z.array(z.string()).default([]),
})
export type CreateCommentInput = z.infer<typeof createCommentSchema>

/**
 * Seguimiento respaldado por Plane.so. El panel NO gestiona el estado:
 * guarda la referencia al work item y lee su estado desde Plane.
 */
export const followupStateGroupSchema = z.enum([
  'todo',
  'in_progress',
  'done',
  'cancelled',
])
export type FollowupStateGroup = z.infer<typeof followupStateGroupSchema>

export const FOLLOWUP_STATE_GROUP_LABELS: Record<FollowupStateGroup, string> = {
  todo: 'Por hacer',
  in_progress: 'En curso',
  done: 'Hecho',
  cancelled: 'Cancelado',
}

export const followupSchema = z.object({
  id: z.string(),
  entityType: entityTypeSchema,
  entityId: z.string(),
  /** Referencia al work item. La gestión vive allá. */
  planeWorkItemId: z.string(),
  /** Proyecto de Plane donde vive el work item. Necesario para el deep link. */
  planeProjectId: z.string().nullable(),
  /** Identificador legible del work item, ej. "OPS-142". Es el label del link. */
  planeIdentificador: z.string().nullable(),
  /** Link directo para abrirlo en Plane. */
  planeUrl: z.string().url(),
  titulo: z.string(),
  /** Nombre del estado tal como lo llama Plane (texto libre del workspace). */
  estado: z.string(),
  /** Agrupación normalizada, para poder filtrar sin conocer el workspace. */
  estadoGroup: followupStateGroupSchema,
  owner: userRefSchema.nullable(),
  fechaObjetivo: z.string().nullable(),
  createdBy: userRefSchema,
  createdAt: z.string(),
})
export type Followup = z.infer<typeof followupSchema>

export const createFollowupSchema = z.object({
  entityType: entityTypeSchema,
  entityId: z.string(),
  titulo: z.string().min(1).max(200),
  descripcion: z.string().max(2000).default(''),
  ownerId: z.string(),
  fechaObjetivo: z.string().nullable().default(null),
})
export type CreateFollowupInput = z.infer<typeof createFollowupSchema>

/** Seguimientos agrupados por dueño, como los muestra Overview. */
export const followupsByOwnerSchema = z.array(
  z.object({
    owner: userRefSchema.nullable(),
    followups: z.array(followupSchema),
  }),
)
export type FollowupsByOwner = z.infer<typeof followupsByOwnerSchema>
